/**
 * useAppSettingHistory -- per-key change history (P44.2 / #583).
 *
 * Walks GET /v1/admin/settings/app/{key}/history newest-first.
 * Admin-only on the backend; callers gate the surrounding UI with
 * useMe(). Cache invalidates on each PATCH via the
 * useAppSettingPatch mutation hook (P43.2 already invalidates the
 * shared "admin-settings" queryKey; we use a separate key here so
 * the history view re-fetches without piggy-backing on the settings
 * grid invalidation).
 */
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { api } from "shared/api/client";
import type { AppSettingHistoryRow } from "shared/api/types";

export function useAppSettingHistory(
  key: string | null,
  options: { enabled?: boolean; limit?: number } = {},
): UseQueryResult<AppSettingHistoryRow[]> {
  const limit = options.limit ?? 50;
  return useQuery<AppSettingHistoryRow[]>({
    queryKey: ["admin-settings-history", key, limit] as const,
    queryFn: () =>
      api.get<AppSettingHistoryRow[]>(
        `/v1/admin/settings/app/${encodeURIComponent(key ?? "")}/history?limit=${limit}`,
      ),
    enabled: (options.enabled ?? true) && Boolean(key),
  });
}
